// The game screen (T3.8/T3.9): chrome around GameBoard — player bars, tray,
// move list, menu, end-of-game beat and result overlay.
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ListAltIcon from '@mui/icons-material/ListAlt';
import { Alert, Box, Button, CircularProgress, IconButton, Paper, Typography, Snackbar } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { cellCenter } from '../board/hexGeometry';
import { GameBoard } from '../board/GameBoard';
import { HandTray } from '../board/HandTray';
import type { GameController } from '../controller/GameController';
import { useGameController } from '../controller/useGameController';
import type { BugKind } from '@hive/engine';
import { GameMenu } from './GameMenu';
import { useGameSettings } from './gameSettings';
import { MoveList } from './MoveList';
import { PieceGuideButton } from './PieceGuide';
import { PieceInfoCard } from './PieceInfoCard';
import { PlayerBar } from './PlayerBar';
import { ResultBanner, ResultOverlay } from './ResultOverlay';
import { SettingsGear } from './SettingsGear';

export function GameScreen({
  controller,
  staticMode = false,
  onRematch,
  playerNames,
}: {
  controller: GameController;
  staticMode?: boolean;
  /** Multiplayer (T4.7): overlay offers Rematch instead of New game. */
  onRematch?: () => void;
  /** Multiplayer: real display names for the player bars. */
  playerNames?: { white: string | null; black: string | null };
}) {
  const snap = useGameController(controller);
  const { confirmMove } = useGameSettings();
  const [movesOpen, setMovesOpen] = useState(false);
  const [dismissedNotice, setDismissedNotice] = useState(0);
  const [pieceInfo, setPieceInfo] = useState<BugKind | null>(null);
  const beatTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Keep the controller's staging behaviour in sync with the setting.
  useEffect(() => {
    controller.setConfirmMove(confirmMove);
  }, [controller, confirmMove]);

  // Debug surface for validators.
  useEffect(() => {
    (window as unknown as { __hive?: unknown }).__hive = { controller };
  }, [controller]);

  // A move stuck on a dropped connection re-sends itself the moment the browser
  // reports it is back online (the controller no-ops unless one is stuck).
  useEffect(() => {
    const onOnline = () => controller.retryPending();
    window.addEventListener('online', onOnline);
    return () => window.removeEventListener('online', onOnline);
  }, [controller]);

  // The end-of-game beat (§6.3): center the surrounded queen, pulse its ring,
  // pause ~1s (tap to skip), then the overlay.
  useEffect(() => {
    if (!snap.beat) return;
    const { x, y } = cellCenter(snap.beat.center);
    controller.setView({ cx: x, cy: y, zoom: 1.4 });
    if (staticMode) return; // gallery: hold the beat frame
    beatTimer.current = setTimeout(() => controller.finishBeat(), 1100);
    const skip = () => controller.finishBeat();
    window.addEventListener('pointerdown', skip, { once: true });
    return () => {
      if (beatTimer.current) clearTimeout(beatTimer.current);
      window.removeEventListener('pointerdown', skip);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [!!snap.beat]);

  return (
    <Box sx={{ height: '100dvh', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', px: 1, py: 0.5, gap: 0.5 }}>
        <IconButton component={RouterLink} to="/lobby" aria-label="back to lobby" size="small">
          <ArrowBackIcon fontSize="small" />
        </IconButton>
        <Typography variant="subtitle1" fontWeight={700} letterSpacing="0.15em" sx={{ flex: 1 }}>
          HIVE
        </Typography>
        <PieceGuideButton />
        <IconButton aria-label="move list" size="small" onClick={() => setMovesOpen(true)}>
          <ListAltIcon fontSize="small" />
        </IconButton>
        <SettingsGear />
        <GameMenu controller={controller} snap={snap} />
      </Box>
      <ResultBanner controller={controller} snap={snap} />
      {/* Multiplayer: opponent on top, your seat at the bottom (chess-app
          convention); hot-seat keeps black-over-white. */}
      <PlayerBar snap={snap} color={snap.myColor === 'b' ? 'w' : 'b'} names={playerNames} />
      <Box sx={{ flex: 1, minHeight: 0, position: 'relative' }}>
        <GameBoard controller={controller} onPieceInfo={setPieceInfo} />
        <PieceInfoCard kind={pieceInfo} />
        {/* The toss is performed by moving the ADJACENT piece — without this
            hint the pillbug's power looks broken (its own targets are just
            one-step slides). */}
        {snap.tossHint && !snap.drag && (
          <Paper
            elevation={4}
            data-testid="toss-hint"
            sx={{
              position: 'absolute',
              top: 8,
              left: '50%',
              transform: 'translateX(-50%)',
              maxWidth: 'min(92%, 360px)',
              px: 1.5,
              py: 1,
              pointerEvents: 'none',
              zIndex: 2,
            }}
          >
            <Typography variant="caption">
              <b>Toss:</b> this piece can also throw its neighbours — tap the piece next to it you
              want to move, then drop it on a free space beside this one.
            </Typography>
          </Paper>
        )}
      </Box>
      <PlayerBar snap={snap} color={snap.myColor === 'b' ? 'b' : 'w'} names={playerNames} />
      {/* Confirm-move bar: only present when the setting is on. Confirm is
          disabled until a move is staged; Cancel discards the staged preview. */}
      {snap.confirmMove && !snap.end && (
        <Box sx={{ display: 'flex', gap: 1, px: 1, py: 0.5 }}>
          <Button
            variant="outlined"
            size="small"
            disabled={!snap.staged}
            onClick={() => controller.discardStaged()}
            data-testid="cancel-move"
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            size="small"
            fullWidth
            disabled={!snap.staged}
            onClick={() => controller.confirmStaged()}
            data-testid="confirm-move"
          >
            Confirm move
          </Button>
        </Box>
      )}
      {/* Persist status: reassure while a move is saving, and — critically for a
          game-ending move — surface a durable, actionable failure instead of a
          move that silently never reached the backend. */}
      {snap.syncStatus === 'error' ? (
        <Alert
          severity="warning"
          data-testid="sync-error"
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => controller.retryPending()}
              data-testid="sync-retry"
            >
              Retry
            </Button>
          }
          sx={{ mx: 1, mb: 0.5, py: 0, alignItems: 'center' }}
        >
          Move not saved — check your connection.
        </Alert>
      ) : snap.syncStatus === 'saving' ? (
        <Box
          data-testid="sync-saving"
          sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 0.25 }}
        >
          <CircularProgress size={12} thickness={6} />
          <Typography variant="caption" color="text.secondary">
            Saving…
          </Typography>
        </Box>
      ) : null}
      <Box sx={{ px: 1, pb: 'max(4px, env(safe-area-inset-bottom))' }}>
        <HandTray controller={controller} />
      </Box>
      <MoveList log={snap.log} open={movesOpen} onClose={() => setMovesOpen(false)} />
      <ResultOverlay controller={controller} snap={snap} {...(onRematch ? { onRematch } : {})} />
      <Snackbar
        key={snap.notice?.id ?? 0}
        open={!!snap.notice && snap.notice.id !== dismissedNotice}
        autoHideDuration={4000}
        onClose={() => setDismissedNotice(snap.notice?.id ?? 0)}
        message={snap.notice?.text}
        data-testid="notice-toast"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
}
